/**
 * Recursively formats react-hook-form errors into a flat array of field paths with messages.
 *
 * This function walks through the nested error object structure from react-hook-form,
 * handling both nested objects and arrays (e.g., customerLineItems[0].label).
 *
 * @param errors - The errors object from react-hook-form's SubmitErrorHandler
 * @param parentPath - The current path prefix (used internally for recursion)
 * @returns Array of objects containing field paths and optional error messages
 *
 * @example
 * // Input error object:
 * {
 *   customer: { externalTMSID: { message: 'Required' } },
 *   rateData: {
 *     customerLineItems: [
 *       { label: { message: 'Required' } },
 *       { quantity: { message: 'Must be positive' } }
 *     ]
 *   }
 * }
 *
 * // Output:
 * [
 *   { path: 'customer.externalTMSID', message: 'Required' },
 *   { path: 'rateData.customerLineItems.0.label', message: 'Required' },
 *   { path: 'rateData.customerLineItems.1.quantity', message: 'Must be positive' }
 * ]
 */
export function formatFormErrors(
  errors: any,
  parentPath = ''
): Array<{ path: string; message?: string }> {
  const out: Array<{ path: string; message?: string }> = [];

  if (!errors || typeof errors !== 'object') return out;

  for (const key of Object.keys(errors)) {
    const val = errors[key];
    const path = parentPath ? `${parentPath}.${key}` : key;

    // Handle arrays (e.g., customerLineItems, commodities)
    if (Array.isArray(val)) {
      val.forEach((item, idx) => {
        out.push(...formatFormErrors(item, `${path}.${idx}`));
      });
      continue;
    }

    // Check if this node has a direct error message
    const hasMessage = val && typeof val === 'object' && 'message' in val;
    if (hasMessage) {
      out.push({ path, message: val.message });
    } else if (val && typeof val === 'object') {
      // Recursively process nested objects (only if no message was found)
      out.push(...formatFormErrors(val, path));
    }
  }

  return out;
}
